<template>
  <y-autocomplete
    ref="autocomplete"
    value-key="address"
    value-template="{address}"
    title-template="{address}"
    placeholder="Улица, дом"
    description-template="{region}"
    icon-variant="primary"
    icon-name="home"
    hide-no-data-text
    :label="$attrs.label || 'Адрес'"
    :items="normalizedItems"
    :use-spinner="useSpinner"
    :modelValue="modelValue"
    :max-length="maxLength"
    @blur="onBlur"
    @search="search"
    @focus="setRestrictedItems"
    v-bind="$attrs"
  >
    <div v-if="useMap" class="vz-direction-map-btn" @click="showMapModal">
      <vz-icon
        name="map-marker"
        variant="primary"
        :size="16"
      />
      <span class="fs-16" :class="{'color-inactive': $attrs.disabled}">На карте</span>
    </div>
    <slot/>
  </y-autocomplete>

  <client-only v-if="useMap">
    <vz-map-select-modal
      ref="mapSelectModal"
      :location="location"
      :address="modelValue"
      :hide-switcher="!showSwitcher"
      :terminal-purpose="terminalPurpose"
      :direction-type="directionType"
      @submit="onMapSubmit"
    />
  </client-only>
</template>

<script setup lang="ts">
import {searchAddressesByLocation} from '@/api/city'
import {DaDataRecord, Location, OrderDirectionAddress, TerminalPurpose} from '~/types/types'
import {PropType} from 'vue'
import VzMapSelectModal from '~/components/modals/vz-map-select-modal.vue';
import YAutocomplete from '~/components/ui-kit/y-autocomplete.vue';

// Условно наследуем компонент от YAutocomplete
defineOptions({
  inheritAttrs: false
})

const emit = defineEmits(['map-submit'])

const props = defineProps({
  location: {
    type: Object as PropType<Location>,
    default: () => {}
  },
  modelValue: {
    type: String,
    default: ''
  },
  useMap: {
    type: Boolean,
    default: false
  },
  showSwitcher: {
    type: Boolean,
    default: false
  },
  suggestionOff: {
    type: Boolean,
    default: false
  },
  maxLength: {
    type: Number,
    default: null
  },
  terminalPurpose: {
    type: String as PropType<'' | TerminalPurpose>,
    default: ''
  },
  restrictedItems: {
    type: Object as PropType<[]>,
    default: []
  },
  directionType: {
    type: String,
    default: 'address'
  }
})
const attr = useAttrs()

const mapSelectModal = ref<typeof VzMapSelectModal>()
const autocomplete = ref()

const items = ref<DaDataRecord[]>([])
const changeAfterSearch = ref(false)
const useSpinner = ref(false)

const normalizedItems = computed((): OrderDirectionAddress[] => {
  return normalizeSearchingAddress(items.value || [])
})

const convertRestrictedItems = computed(() => {
  return props.restrictedItems.map((val: any): any => {
    return {
      value: val.address,
      region: `${props.location?.type || ''} ${props.location?.name || ''}`,
      data: {
        district_fias: null,
        fias: props.location?.fias || null,
        geo: {
          lat: val.latitude,
          lon: val.longitude
        },
        region_fias: props.location?.region_fias || null,
        region_s: props.location?.region_s || null
        // unfamiliar: data?.unfamiliar,
      },
      locationGuid: props.location?.guid,
      offerWaitingTime: 0
    }
  })
})

function setRestrictedItems() {
  if (props.restrictedItems?.length > 0) {
    changeAfterSearch.value = false
    search(props.modelValue)
  }
}

async function search(search: string): Promise<any> {
  if (props.restrictedItems?.length > 0) {
    items.value = convertRestrictedItems.value
    return
  }

  if (!props.location) {
    return
  }
  useSpinner.value = true
  const customAddress = {
    value: search,
    region: 'Внимание <span class="font-italic">Это адрес, указанный вручную. </span>' +
      '<span class="text-bold"><br/>Выбор этого варианта может увеличить цену заказа</span>',
    data: {
      geo: {lat: '0', lon: '0'}
    }
  } as DaDataRecord

  items.value = await searchAddressesByLocation(search, props.location, {suggestionOff: props.suggestionOff}).finally(() => {
    useSpinner.value = false
  })
  if (search) {
    items.value.push(customAddress)
  }
  if (changeAfterSearch.value && search) {
    await nextTick()
    autocomplete.value?.onSelect()
  }
  changeAfterSearch.value = false
}

function onBlur() {
  changeAfterSearch.value = true
}

function showMapModal() {
  if (attr.disabled) {
    return
  }
  autocomplete.value?.close()
  mapSelectModal.value?.show()
}

function onMapSubmit(data: any) {
  emit('map-submit', data)
}

function normalizeSearchingAddress(items: DaDataRecord[]): OrderDirectionAddress[] {
  const cache: OrderDirectionAddress[] = []
  items.forEach(({ value, region, data, locationGuid, offerWaitingTime, daDataShame }) => {
    cache.push({
      geo: data?.geo,
      region,
      address: value,
      fias: data?.fias,
      unfamiliar: data?.unfamiliar,
      region_fias: data?.region_fias,
      district_fias: data?.district_fias,
      locationGuid,
      region_s: data?.region_s,
      offerWaitingTime,
      daDataShame
    })
  })
  return cache
}

watch(() => props.location,
  () => {
    changeAfterSearch.value = false
    search(props.modelValue)
  }
)

// Если адрес стоит, но списка нет, или значения нет в списке, то формируем список.
watch(() => props.modelValue,
  () => {
    if (!items.value.length || !items.value.find(item => item.value === props.modelValue)) {
      search(props.modelValue as string)
    }
  },
  {immediate: true}
)

defineExpose({
  search
})
</script>

<style scoped>

</style>
